import React, { useState, useEffect, useCallback } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { toast } from 'react-toastify';

import api from '~/services/api';
import Loading from '~/components/Loading';
import ConfirmModal from './ConfirmModal';

export default function TransferModal({ isOpen, onClose, onTransfer }) {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const loadAgents = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/support/whatsapp/agents');
      setAgents(data.agents || []);
    } catch (err) {
      toast.error('Erro ao carregar agentes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadAgents();
      setSelectedAgent(null);
    }
  }, [isOpen, loadAgents]);

  const handleConfirm = useCallback(() => {
    if (!selectedAgent) {
      toast.warning('Selecione um agente para transferir.');
      return;
    }
    setShowConfirm(true);
  }, [selectedAgent]);

  const handleConfirmTransfer = useCallback(() => {
    setShowConfirm(false);
    onTransfer(selectedAgent);
  }, [selectedAgent, onTransfer]);

  return (
    <Modal isOpen={isOpen} toggle={onClose}>
      <ModalHeader toggle={onClose}>Transferir atendimento</ModalHeader>
      <ModalBody>
        {loading ? (
          <Loading />
        ) : (
          <>
            <div className="font-size-14 mb-3">
              Selecione o agente que receberá este atendimento:
            </div>
            {agents.length === 0 ? (
              <div className="text-muted font-size-14">
                Nenhum agente disponível para transferência.
              </div>
            ) : (
              agents.map(agent => {
                const isSelected = selectedAgent === agent.id;
                return (
                  <div
                    key={agent.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedAgent(agent.id)}
                    style={{
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: isSelected
                        ? '1px solid #185FA5'
                        : '0.5px solid #dee2e6',
                      background: isSelected ? '#E6F1FB' : '#fff',
                      marginBottom: '6px',
                      cursor: 'pointer',
                      outline: 'none',
                      transition: 'all 0.15s',
                    }}
                  >
                    <div
                      className="d-flex align-items-center"
                      style={{ gap: '10px' }}
                    >
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: isSelected ? '#185FA5' : '#f2f2f0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 500,
                          fontSize: '12px',
                          color: isSelected ? '#fff' : '#5F5E5A',
                          flexShrink: 0,
                        }}
                      >
                        {agent.name ? agent.name[0].toUpperCase() : '?'}
                      </div>
                      <div>
                        <div
                          className="font-size-13 font-weight-600"
                          style={{
                            color: isSelected ? '#0C447C' : '#3d3d3a',
                          }}
                        >
                          {agent.name}
                        </div>
                        <div
                          className="font-size-11"
                          style={{ color: '#888780' }}
                        >
                          {agent.email}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button className="new-layout-btn main" color="light" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          className="new-layout-btn main"
          color="primary"
          disabled={!selectedAgent}
          onClick={handleConfirm}
        >
          Transferir
        </Button>
      </ModalFooter>
      <ConfirmModal
        isOpen={showConfirm}
        title="Transferir atendimento"
        message={`Transferir para ${
          agents.find(a => a.id === selectedAgent)?.name || 'agente'
        }?`}
        confirmLabel="Transferir"
        confirmColor="primary"
        onConfirm={handleConfirmTransfer}
        onCancel={() => setShowConfirm(false)}
      />
    </Modal>
  );
}
